import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate, useParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { getProjectPublicUrl } from "../../utils/projectDeployment";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";
import { TRIAL_LANDING_EVENT } from "../../utils/trialOnboarding";
import LandingSectionDialog from "../../components/dashboard/landingEditor/LandingSectionDialog";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import {
  emptySettings,
  mergeSettings,
} from "../../components/dashboard/landingEditor/config";
import { settingsPayload } from "../../components/dashboard/landingEditor/editorUtils";
import {
  findStepForSection,
  getSiteBuilderProgress,
} from "../../components/dashboard/landingEditor/siteBuilderModel";
import LandingStudioToolbar from "../../components/dashboard/landingStudio/LandingStudioToolbar";
import LandingStudioQuestionDock from "../../components/dashboard/landingStudio/LandingStudioQuestionDock";
import LandingStudioCanvasStage from "../../components/dashboard/landingStudio/LandingStudioCanvasStage";
import LandingSegmentInspector from "../../components/dashboard/landingStudio/LandingSegmentInspector";
import { resolveNextStudioQuestion } from "../../components/dashboard/landingStudio/landingStudioQuestions";
import {
  setManyValuesAtPaths,
  setValueAtPath,
} from "../../components/dashboard/landingStudio/landingStudioUtils";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ProjectLandingVisualEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const mobileViewport = useMediaQuery("(max-width:700px)");
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();

  const [project, setProject] = React.useState(null);
  const [form, setForm] = React.useState(emptySettings);
  const [device, setDevice] = React.useState(() => (mobileViewport ? "mobile" : "desktop"));
  const [activeSection, setActiveSection] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [savingPath, setSavingPath] = React.useState("");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState(0);
  const [sectionOpen, setSectionOpen] = React.useState(false);
  const [sectionId, setSectionId] = React.useState("hero");
  const [segmentInspectorOpen, setSegmentInspectorOpen] = React.useState(false);
  const [segmentInspectorTool, setSegmentInspectorTool] = React.useState("content");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const publicUrl = getProjectPublicUrl(effectiveProject);
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const question = React.useMemo(
    () => resolveNextStudioQuestion(progress),
    [progress]
  );
  const saving = Boolean(savingPath);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        if (nextProject) setActiveProject(nextProject);
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await settingsApi.get(projectId);
        if (!active) return;
        const merged = mergeSettings(data.settings);
        setForm(merged);

        const initialProgress = getSiteBuilderProgress(merged, productsEnabled);
        const firstIncomplete = initialProgress.steps.find((step) => !step.done);
        const firstSection = firstIncomplete?.actions?.find((action) => action.type === "section")?.section;
        setActiveSection(firstSection || "hero");

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );
      } catch (err) {
        if (active) setError(err.message || "No se pudo cargar la configuración de la página");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [productsEnabled, projectId]);

  const persist = React.useCallback(
    async (nextForm, key = "landing", previousForm = form) => {
      setForm(nextForm);
      try {
        setSavingPath(key);
        setError("");
        const data = await settingsApi.update(
          projectId,
          settingsPayload(nextForm, productsEnabled)
        );
        const merged = mergeSettings(data.settings);
        setForm(merged);
        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );
        setMessage("Cambio guardado");
        return merged;
      } catch (err) {
        setForm(previousForm);
        setError(err.message || "No se pudo guardar el cambio");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [form, productsEnabled, projectId]
  );

  const commitPath = React.useCallback(
    async (path, value) => {
      const previous = form;
      const next = setValueAtPath(previous, path, value);
      return persist(next, `inline:${path}`, previous);
    },
    [form, persist]
  );

  const commitMany = React.useCallback(
    async (changes) => {
      const previous = form;
      const next = setManyValuesAtPaths(previous, changes);
      return persist(next, "inline:multiple", previous);
    },
    [form, persist]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El servidor no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deleteAssetFile = React.useCallback(
    async (path) => {
      if (!isManagedLandingAsset(path)) return { ok: true, removed: false };
      return landingAssetsApi.remove(projectId, path);
    },
    [projectId]
  );

  const uploadInlineImage = React.useCallback(
    async (path, file, metadata = {}) => {
      const previous = form;
      try {
        setSavingPath(`asset:${path}`);
        const asset = await uploadAssetFile(file, metadata);
        const next = setValueAtPath(previous, path, asset.value);
        return await persist(next, `asset:${path}`, previous);
      } catch (err) {
        setError(err.message || "No se pudo subir la imagen");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [form, persist, uploadAssetFile]
  );

  const removeInlineImage = React.useCallback(
    async (path) => {
      const previous = form;
      const currentValue = String(path || "")
        .split(".")
        .filter(Boolean)
        .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], previous);

      try {
        setSavingPath(`asset:${path}`);
        if (currentValue) await deleteAssetFile(currentValue);
        const next = setValueAtPath(previous, path, "");
        return await persist(next, `asset:${path}`, previous);
      } catch (err) {
        setError(err.message || "No se pudo eliminar la imagen");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [deleteAssetFile, form, persist]
  );

  const openSettingsAt = React.useCallback((tab = 0) => {
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  }, []);

  const openSection = React.useCallback((nextSection) => {
    setActiveSection(nextSection);
    setSectionId(nextSection);
    setSectionOpen(true);
  }, []);

  const runAction = React.useCallback(
    (action) => {
      if (!action) return;
      if (action.type === "section" && action.section) {
        openSection(action.section);
        return;
      }
      openSettingsAt(Number(action.tab || 0));
    },
    [openSection, openSettingsAt]
  );

  const selectProgressStep = React.useCallback(
    (step) => {
      const firstSection = step?.actions?.find((action) => action.type === "section");
      runAction(firstSection || step?.actions?.[0]);
    },
    [runAction]
  );

  const selectCanvasSection = React.useCallback(
    (nextSection) => {
      const relatedStep = findStepForSection(nextSection, productsEnabled);
      if (relatedStep || nextSection) setActiveSection(nextSection);
    },
    [productsEnabled]
  );

  const openSegmentTool = React.useCallback((nextSection, tool = "content") => {
    setActiveSection(nextSection);
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorOpen(true);
  }, []);

  const openSegmentTexts = React.useCallback((nextSection) => {
    setSegmentInspectorOpen(false);
    openSection(nextSection);
  }, [openSection]);

  const leaveEditor = () => {
    navigate(`/dashboard/proyectos/${projectId}/configuracion/general`);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.3} alignItems="center">
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
            Preparando la página editable…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (effectiveProject && !landingEnabled) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2, bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.5} sx={{ maxWidth: 520, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
            La página web no está habilitada
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,.62)" }}>
            Este proyecto todavía no tiene habilitado el módulo de landing.
          </Typography>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={leaveEditor} sx={{ alignSelf: "center" }}>
            Volver al panel
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F4F5F7" }}>
      <LandingStudioToolbar
        title={form.business?.name || effectiveProject?.name || "Tu página web"}
        publicStatus={form.publicStatus}
        revision={form.revision}
        device={device}
        saving={saving}
        canOpenPublic={Boolean(publicUrl)}
        onBack={leaveEditor}
        onChangeDevice={setDevice}
        onOpenSettings={() => openSettingsAt(0)}
        onOpenPublic={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
      />

      <LandingStudioCanvasStage
        form={form}
        device={device}
        productsEnabled={productsEnabled}
        activeSection={activeSection}
        saving={saving}
        onSelectSection={selectCanvasSection}
        onCommit={commitPath}
        onCommitMany={commitMany}
        onUploadImage={uploadInlineImage}
        onRemoveImage={removeInlineImage}
        onOpenSegmentTool={openSegmentTool}
      />

      <LandingStudioQuestionDock
        question={question}
        progress={progress}
        onResolve={() => runAction(question.action)}
        onSelectStep={selectProgressStep}
      />


      <LandingSegmentInspector
        open={segmentInspectorOpen}
        sectionId={sectionId}
        initialTool={segmentInspectorTool}
        form={form}
        productsEnabled={productsEnabled}
        saving={saving}
        onClose={() => setSegmentInspectorOpen(false)}
        onCommit={commitPath}
        onCommitMany={commitMany}
        onOpenTexts={openSegmentTexts}
        onUploadImage={uploadInlineImage}
        onRemoveImage={removeInlineImage}
      />

      <LandingSectionDialog
        open={sectionOpen}
        activeSection={sectionId}
        form={form}
        productsEnabled={productsEnabled}
        saving={savingPath.startsWith("section:")}
        onClose={() => setSectionOpen(false)}
        onSave={(nextForm) => persist(nextForm, `section:${sectionId}`, form)}
        onOpenSettings={openSettingsAt}
      />

      <LandingSettingsDialog
        open={settingsOpen}
        form={form}
        productsEnabled={productsEnabled}
        saving={savingPath === "settings"}
        initialTab={settingsInitialTab}
        showTrialGuide={!progress.complete}
        onClose={() => setSettingsOpen(false)}
        onSave={(nextForm) => persist(nextForm, "settings", form)}
        onUploadAsset={uploadAssetFile}
        onDeleteAsset={deleteAssetFile}
      />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={2200}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMessage("")} sx={{ fontWeight: 850 }}>
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5200}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ fontWeight: 850 }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
